import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Modal, ModalDialog, Box, Typography, Button, Stack, Avatar, Chip } from '@mui/joy';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';

import { AppContext } from '../../context';
import {
    APIWorkstationReassignRequestsPoll,
    APIWorkstationReassignRequestRespond,
    APIWorkstationReassignRequestAck,
    parseApiError,
} from '../../api';

const POLL_MS = 5 * 60 * 1000;   // 5 минут
const getInitials = (name = '') =>
    name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase();

export default function ReassignRequestsWatcher() {
    const { token, activeShift, showToast } = useContext(AppContext);
    const [incoming, setIncoming] = useState([]);      // входящие pending-запросы (к нам)
    const [responding, setResponding] = useState(false);
    const ackedRef = useRef(new Set());                // id исходящих, по которым уже показали тост

    const poll = useCallback(async () => {
        if (!token || !activeShift) return;
        const res = await APIWorkstationReassignRequestsPoll(token);
        if (!res || res.error) return;

        setIncoming(Array.isArray(res.incoming) ? res.incoming : []);

        const outgoing = Array.isArray(res.outgoing) ? res.outgoing : [];
        for (const r of outgoing) {
            if (ackedRef.current.has(r.id)) continue;
            ackedRef.current.add(r.id);
            if (r.status === 'accepted') {
                showToast(`Приняли фотографа ${r.photographer_name}`, 'success');
            } else if (r.status === 'rejected') {
                showToast(`Отклонён запрос на ${r.photographer_name}`, 'warning');
            }
            APIWorkstationReassignRequestAck(token, r.id);   // чтобы тост не повторился
        }
    }, [token, activeShift, showToast]);

    // Опрос: сразу + раз в 5 минут, пока идёт смена
    useEffect(() => {
        if (!token || !activeShift) return;
        poll();
        const id = setInterval(() => { if (!document.hidden) poll(); }, POLL_MS);
        return () => clearInterval(id);
    }, [token, activeShift, poll]);

    const current = incoming[0] || null;

    const respond = async (action) => {
        if (!current) return;
        setResponding(true);
        const res = await APIWorkstationReassignRequestRespond(token, current.id, action);
        setResponding(false);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось ответить'), 'danger');
            return;
        }
        setIncoming(prev => prev.filter(r => r.id !== current.id));
        showToast(
            action === 'accept'
                ? `Фотограф ${current.photographer_name} передан`
                : `Запрос на ${current.photographer_name} отклонён`,
            action === 'accept' ? 'success' : 'neutral',
        );
        poll();
    };

    if (!current) return null;

    return (
        <Modal open onClose={() => {}} sx={{ zIndex: 20000 }}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 420, width: '100%', p: 2.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <SwapHorizRoundedIcon sx={{ fontSize: 20, color: 'var(--joy-palette-primary-400)' }} />
                    <Typography level="h4" sx={{ fontWeight: 800 }}>Запрос на фотографа</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 1.5 }}>
                    Точка «{current.requester_location_name}» (админ {current.requester_admin_name})
                    просит забрать фотографа с вашей смены.
                </Typography>

                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.2,
                    px: 1.4, py: 1.1, mb: 2,
                    bgcolor: 'var(--joy-palette-background-level2)',
                    borderRadius: 'var(--joy-radius-md)',
                }}>
                    <Avatar src={current.photographer_avatar_url} size="sm"
                        sx={{ width: 40, height: 40, fontWeight: 700 }}>
                        {getInitials(current.photographer_name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                            {current.photographer_name}
                        </Typography>
                        <Chip size="sm" variant="soft" color="warning"
                            sx={{ mt: 0.3, fontSize: 10, fontWeight: 700 }}>
                            → {current.requester_location_name}
                        </Chip>
                    </Box>
                </Box>

                <Stack direction="row" spacing={1}>
                    <Button fullWidth variant="soft" color="neutral" loading={responding}
                        onClick={() => respond('reject')} sx={{ fontWeight: 700 }}>
                        Отклонить
                    </Button>
                    <Button fullWidth variant="solid" color="primary" loading={responding}
                        onClick={() => respond('accept')} sx={{ fontWeight: 800 }}>
                        Принять
                    </Button>
                </Stack>

                {incoming.length > 1 && (
                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', textAlign: 'center', mt: 1.5 }}>
                        Ещё запросов в очереди: {incoming.length - 1}
                    </Typography>
                )}
            </ModalDialog>
        </Modal>
    );
}